<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { GoogleMap, Marker } from "vue3-google-map";
import PlacesAutocomplete from "@/components/atoms/map/PlacesAutocomplete.vue";
import Combobox from "@/components/ui/Combobox.vue";

const DEFAULT_CENTER = {
  lat: 45.188529,
  lng: 5.7245239
};

const props = defineProps({
  currentLocation: {
    default: null
  },
  countries: {
    type: Array,
    required: true,
    default: () => []
  },
  errors: {
    type: Object,
    required: false,
    default: () => ({})
  }
});

const disablePlaceSearch = ref(false);
const mapCenter = ref(DEFAULT_CENTER);
const markerCoords = ref(null);
const location = reactive({
  name: "",
  address: "",
  country: null,
  coords: DEFAULT_CENTER,
  description: ""
});
const emit = defineEmits(['update:location']);
watch(
  () => location,
  (newLocation) => {
    emit('update:location', {
      ...newLocation,
      latitude: newLocation.coords ? newLocation.coords.lat : null,
      longitude: newLocation.coords ? newLocation.coords.lng : null
    });
  },
  { deep: true }
);

const GoogleMapAPIKey = computed(() => window.Laravel.gmapApiKey);
const center = computed(() => location.coords);
const latitude = computed(() => location.coords ? location.coords.lat : null);
const longitude = computed(() => location.coords ? location.coords.lng : null);
const countryOptions = computed(() => props.countries.map(country => ({ value: country.id, label: country.name })));

function setPlace(place) {
  if (place.formatted_address) {
    location.name = place.name;
    location.address = place.formatted_address;

    if (place.geometry && place.geometry.location) {
      let coords = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      location.coords = coords;
      mapCenter.value = coords;
      markerCoords.value = coords;
    }
    if (place.address_components) {
      var country = place.address_components.find(function(ac) {
        return ac.types.includes("country");
      });
      if (country) {
        location.country = country.short_name;
      }
    }
  }
}

function updateCoords(e) {
  markerCoords.value = {
    lat: e.latLng.lat(),
    lng: e.latLng.lng()
  };
  location.coords = markerCoords.value;
  mapCenter.value = markerCoords.value;
}

onMounted(() => {
  if (props.currentLocation) {
    let coords = {
      lat: props.currentLocation.latitude,
      lng: props.currentLocation.longitude
    };
    location.name = props.currentLocation.name;
    location.address = props.currentLocation.address;
    location.country = props.currentLocation.country_code;
    location.coords = coords;
    location.description = props.currentLocation.description;
    markerCoords.value = coords;
    mapCenter.value = coords;
  }
});
</script>
<template>
  <div>
    <div class="bg-light p-3 mb-2">
      <div class="form-group">
        <label for="frm-location-search">{{ $t("Search a location") }}</label>
        <PlacesAutocomplete
          id="frm-location-search"
          @place-changed="setPlace"
          :disabled="disablePlaceSearch"
          class="form-control" />
      </div>
    </div>
    <div>
      <GoogleMap
        :api-key="GoogleMapAPIKey"
        style="width: 100%; height: 350px"
        :center="center"
        :zoom="15"
      >
        <Marker v-if="markerCoords"
                :options="{
                      position: markerCoords,
                      draggable: true,
                    }"
                @dragend="updateCoords($event)"
        />
      </GoogleMap>


      <small class="text-muted mb-2">
        Latitude : {{ latitude }} - Longitude : {{ longitude }}
      </small>

      <div class="form-group">
        <label for="frm-location-name">{{ $t('Name') }}</label>
        <input type="text" name="name" id="frm-location-name" v-model="location.name" class="form-control" required>
        <template v-if="props.errors?.name">
          <div class="invalid-feedback d-block">
            <div v-for="error in props.errors.name" :key="error">{{ error }}</div>
          </div>
        </template>
      </div>
      <div class="form-group">
        <label for="frm-location-address">{{ $t('Address') }}</label>
        <input type="text" name="address" id="frm-location-address" v-model="location.address" class="form-control"
               required>
        <template v-if="props.errors?.address">
          <div class="invalid-feedback d-block">
            <div v-for="error in props.errors.address" :key="error">{{ error }}</div>
          </div>
        </template>
      </div>
      <div class="form-group">
        <label for="frm-location-country">{{ $t('Country') }}</label>
        <Combobox
          id="frm-location-country"
          name="country"
          v-model="location.country"
          :options="countryOptions"
          :fullwidth="true"
        ></Combobox>
        <template v-if="props.errors?.country">
          <div class="invalid-feedback d-block">
            <div v-for="error in props.errors.country" :key="error">{{ error }}</div>
          </div>
        </template>
      </div>
      <input type="hidden" name="latitude" id="frm-location-latitude" v-model="latitude" class="form-control">
      <input type="hidden" name="longitude" id="frm-location-longitude" v-model="longitude" class="form-control">
      <div class="form-group">
        <label for="frm-location-description">{{ $t('Additional information') }}</label>
        <textarea name="description" id="frm-location-description" cols="30" rows="3" class="form-control"
                  v-model="location.description"></textarea>
      </div>
    </div>
  </div>
</template>
